import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import { ProductIcon, CheckCircleIcon, ClockIcon, ShieldCheckMarkIcon, InfoIcon, PhoneIcon, ChatIcon, QuestionCircleIcon, ChevronDownIcon, ChevronUpIcon, CalendarIcon, PersonIcon, CodeIcon, StarFilledIcon, LightbulbIcon, ThumbsUpIcon, SearchIcon, TargetIcon, MenuHorizontalIcon, XIcon } from '@shopify/polaris-icons';

// Main Landing Page Component
const RetailForgeLanding = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number>(0); // First FAQ open by default
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeFaqTab, setActiveFaqTab] = useState<'general' | 'privacy'>('general');

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    if (!captchaToken) {
      setError('Please complete the security check');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(process.env.REACT_APP_SUBSCRIBE_URL || '/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          captchaToken
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to subscribe');
      }

      setSubmitted(true);
      setEmail('');
      setCaptchaToken('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setCaptchaToken(''); // Reset captcha on error
    } finally {
      setLoading(false);
    }
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? -1 : index);
  };

  const scrollToWaitlist = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
    // Trigger button pulse animation
    setTimeout(() => {
      const button = document.querySelector('#waitlist button');
      button?.classList.add('animate-buttonPulse');
      setTimeout(() => {
        button?.classList.remove('animate-buttonPulse');
      }, 600);
    }, 500);
  };

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      const headerHeight = 80; // Approximate height of sticky header
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/retailforge-logo.png"
                alt="RetailForge - Shopify Partner Building E-Commerce Solutions"
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-2xl font-semibold text-blue-600">RetailForge</h1>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Solutions for the modern storefront</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex gap-8">
              <a href="#about" onClick={(e) => handleSmoothScroll(e, 'about')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium transition-all duration-200 hover:scale-110">
                <InfoIcon className="w-5 h-5" />
                About
              </a>
              <a href="#products" onClick={(e) => handleSmoothScroll(e, 'products')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium transition-all duration-200 hover:scale-110">
                <ProductIcon className="w-5 h-5" />
                Products
              </a>
              <a href="#faq" onClick={(e) => handleSmoothScroll(e, 'faq')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium transition-all duration-200 hover:scale-110">
                <QuestionCircleIcon className="w-5 h-5" />
                FAQ
              </a>
              <a href="#contact" onClick={(e) => handleSmoothScroll(e, 'contact')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium transition-all duration-200 hover:scale-110">
                <PhoneIcon className="w-5 h-5" />
                Contact
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-600 hover:text-slate-900 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <XIcon className="w-6 h-6" />
              ) : (
                <MenuHorizontalIcon className="w-6 h-6" />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 border-t border-slate-200 pt-4 flex flex-col gap-4">
              <a
                href="#about"
                onClick={(e) => { handleSmoothScroll(e, 'about'); setMobileMenuOpen(false); }}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium transition-colors py-2"
              >
                <InfoIcon className="w-5 h-5" />
                About
              </a>
              <a
                href="#products"
                onClick={(e) => { handleSmoothScroll(e, 'products'); setMobileMenuOpen(false); }}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium transition-colors py-2"
              >
                <ProductIcon className="w-5 h-5" />
                Products
              </a>
              <a
                href="#faq"
                onClick={(e) => { handleSmoothScroll(e, 'faq'); setMobileMenuOpen(false); }}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium transition-colors py-2"
              >
                <QuestionCircleIcon className="w-5 h-5" />
                FAQ
              </a>
              <a
                href="#contact"
                onClick={(e) => { handleSmoothScroll(e, 'contact'); setMobileMenuOpen(false); }}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium transition-colors py-2"
              >
                <PhoneIcon className="w-5 h-5" />
                Contact
              </a>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6 relative overflow-hidden bg-gradient-to-b from-white via-white to-slate-50">
        {/* Animated Background */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-80 animate-blob"></div>
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-80 animate-blob" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-80 animate-blob" style={{animationDelay: '4s'}}></div>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 animate-fade-in">
            Intelligent Shopify Apps
            <span className="block text-blue-600">Built by Retail Veterans</span>
          </h2>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed animate-fade-in-delay">
            We understand retail because we've lived it. Now we're building smart tools
            that solve real merchant problems with surgical precision.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto mb-12 animate-slide-up">
            <a href="#products" onClick={(e) => handleSmoothScroll(e, 'products')} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2 whitespace-nowrap">
              <ProductIcon className="w-6 h-6 text-white fill-current" />
              Our Products
            </a>
            <a href="#about" onClick={(e) => handleSmoothScroll(e, 'about')} className="flex-1 bg-white text-slate-700 px-8 py-3 rounded-lg font-semibold border border-slate-300 hover:border-slate-400 transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-2 whitespace-nowrap">
              <InfoIcon className="w-6 h-6" />
              About RetailForge
            </a>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 animate-slide-up-delay">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-blue-600" />
              <span className="text-slate-600 text-sm">10+ years retail experience</span>
            </div>
            <div className="flex items-center gap-2">
              <PersonIcon className="w-5 h-5 text-blue-600" />
              <span className="text-slate-600 text-sm">Founded by working merchants</span>
            </div>
            <div className="flex items-center gap-2">
              <CodeIcon className="w-5 h-5 text-blue-600" />
              <span className="text-slate-600 text-sm">E-Commerce tools for the modern web</span>
            </div>
          </div>
        </div>
      </section>

      {/* StockSentry Preview */}
      <section id="products" className="py-16 px-6 bg-slate-50 relative overflow-hidden">
        {/* Background Illustration with gradient fade */}
        <div
          className="absolute left-0 top-0 bottom-0 w-1/2 pointer-events-none hidden md:block"
          style={{
            backgroundImage: 'linear-gradient(to right, rgba(248, 250, 252, 0.85), rgba(248, 250, 252, 1)), url(/purple-shelves.png)',
            backgroundPosition: 'left center',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
          }}
        />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/stock-sentry-base.png"
                  alt="StockSentry - Intelligent Inventory Management App Logo"
                  className="w-12 h-12 object-contain"
                />
                <div className="flex items-center gap-3">
                  <h3 className="text-3xl font-bold text-slate-900">StockSentry</h3>
                  <span className="inline-flex items-center gap-1.5 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-pulse"></span>
                    Coming Soon
                  </span>
                </div>
              </div>
              <p className="text-lg text-slate-600 mb-6">
                Smart inventory alerts that prevent stockouts before they happen. 
                Unlike basic quantity alerts, StockSentry uses sales velocity and 
                seasonal intelligence to keep you ahead of demand.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 text-green-600 mt-0.5">
                    <CheckCircleIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Velocity-Based Alerts</h4>
                    <p className="text-slate-600">Smart thresholds based on sales speed, not just quantity</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 text-green-600 mt-0.5">
                    <CheckCircleIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Seasonal Intelligence</h4>
                    <p className="text-slate-600">Automatic adjustments for seasonal demand patterns</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 text-green-600 mt-0.5">
                    <CheckCircleIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Multi-Channel Notifications</h4>
                    <p className="text-slate-600">Email, SMS, and Slack alerts when you need them</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 text-center md:text-left">
                <span className="text-sm text-slate-500">Starting at</span>
                <div className="flex items-baseline gap-2 justify-center md:justify-start">
                  <span className="text-3xl font-bold text-blue-600">$7</span>
                  <span className="text-slate-600">/month</span>
                </div>
                <p className="text-sm text-slate-500">14-day free trial • No setup fees</p>
              </div>
            </div>

            {/* Mock Dashboard */}
            <div className="bg-slate-50 rounded-xl p-6 border shadow-lg">
              <div className="bg-white rounded-lg p-4 border" style={{boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.06)'}}>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-slate-900">Inventory Alerts</h4>
                  <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">3 Urgent</span>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">Classic White T-Shirt</p>
                      <p className="text-sm text-slate-600">2 units left • 3.4 units/day velocity</p>
                      <p className="text-xs text-red-600">Stockout in 0.6 days</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">Premium Denim Jacket</p>
                      <p className="text-sm text-slate-600">12 units left • 0.8 units/day velocity</p>
                      <p className="text-xs text-amber-600">Stockout in 15 days</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">Most products healthy</p>
                      <p className="text-sm text-slate-600">47/50 products well-stocked</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Proud Shopify Partner
          </h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            After 10+ years in retail operations, we've experienced every inventory challenge firsthand.
            Stockouts during peak season. Dead inventory draining cash flow. Manual tracking that
            breaks under pressure.
          </p>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            That's why we're building <span className="font-bold text-blue-600">RetailForge</span> – intelligent tools that solve real problems
            with the precision that only comes from experience. As a <a href="https://www.shopify.com/ca/partners" target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-900 hover:text-blue-600 transition-colors duration-200">Shopify Partner</a>, we're committed to
            delivering professional solutions that help merchants succeed.
          </p>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <StarFilledIcon className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-2">Merchant-First Approach</h4>
              <p className="text-slate-600 text-sm">Every feature is designed to solve real merchant challenges and drive business growth</p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <LightbulbIcon className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-2">Seamless Integration</h4>
              <p className="text-slate-600 text-sm">Built natively for Shopify with instant setup and zero technical complexity</p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <ThumbsUpIcon className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-semibold text-slate-900 mb-2">Trusted & Verified</h4>
              <p className="text-slate-600 text-sm">Vetted by Shopify with enterprise-grade security and reliability standards</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 px-6 bg-white relative overflow-hidden">
        {/* Background Illustration with gradient fade */}
        <div
          className="absolute right-0 top-0 bottom-0 w-1/2 pointer-events-none hidden md:block"
          style={{
            backgroundImage: 'linear-gradient(to left, rgba(255, 255, 255, 0.85), rgba(255, 255, 255, 1)), url(/faq-illustration.png)',
            backgroundPosition: 'right center',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
          }}
        />
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-3">Frequently Asked Questions</h2>
          <p className="text-center text-slate-600 mb-8">
            Or get in touch directly at{' '}
            <a
              href="mailto:hello@retailforge.app"
              className="text-slate-900 font-semibold hover:text-blue-600 underline decoration-slate-400 hover:decoration-blue-600 decoration-2 underline-offset-4 transition-colors duration-200"
            >
              hello@retailforge.app
            </a>
          </p>

          {/* FAQ Tabs */}
          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={() => { setActiveFaqTab('general'); setOpenFaqIndex(0); }}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                activeFaqTab === 'general'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <QuestionCircleIcon className={`w-5 h-5 fill-current ${activeFaqTab === 'general' ? 'text-white' : 'text-slate-600'}`} />
                General
              </div>
            </button>
            <button
              onClick={() => { setActiveFaqTab('privacy'); setOpenFaqIndex(0); }}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                activeFaqTab === 'privacy'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <ShieldCheckMarkIcon className={`w-5 h-5 fill-current ${activeFaqTab === 'privacy' ? 'text-white' : 'text-slate-600'}`} />
                Privacy & Security
              </div>
            </button>
          </div>

          {/* General FAQs */}
          {activeFaqTab === 'general' && (
          <div className="space-y-4">
            {/* FAQ Item 1 */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleFaq(0)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ClockIcon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  <h4 className="text-lg font-semibold text-slate-900">When will StockSentry be available?</h4>
                </div>
                {openFaqIndex === 0 ? (
                  <ChevronUpIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                )}
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openFaqIndex === 0 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6">
                  <p className="text-slate-600">
                    We're currently in development and planning to launch by the end of 2025.{' '}
                    <a
                      href="#waitlist"
                      onClick={scrollToWaitlist}
                      className="text-blue-600 hover:text-blue-700 font-medium underline"
                    >
                      Join our waitlist
                    </a>{' '}
                    to be notified when we're ready for beta testing.
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ Item 2 */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleFaq(1)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <SearchIcon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  <h4 className="text-lg font-semibold text-slate-900">How is this different from Shopify's low stock alerts?</h4>
                </div>
                {openFaqIndex === 1 ? (
                  <ChevronUpIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                )}
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openFaqIndex === 1 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6">
                  <p className="text-slate-600">
                    Shopify's alerts are based on fixed quantity thresholds. StockSentry uses sales velocity and seasonal patterns to predict when you'll actually run out, giving you smarter, more actionable warnings.
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ Item 3 */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleFaq(2)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <TargetIcon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  <h4 className="text-lg font-semibold text-slate-900">Will there be a free trial?</h4>
                </div>
                {openFaqIndex === 2 ? (
                  <ChevronUpIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                )}
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openFaqIndex === 2 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6">
                  <p className="text-slate-600">
                    Yes, we'll offer a 14-day free trial so you can see the value before committing. No credit card required to start.
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ Item 4 */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleFaq(3)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <PersonIcon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  <h4 className="text-lg font-semibold text-slate-900">What makes you qualified to build inventory tools?</h4>
                </div>
                {openFaqIndex === 3 ? (
                  <ChevronUpIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                )}
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openFaqIndex === 3 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6">
                  <p className="text-slate-600">
                    We spent over a decade managing inventory for retail businesses. We've lived through every inventory nightmare - from stockouts during peak season to cash tied up in dead stock. We're building the tools we wish we had.
                  </p>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Privacy & Security FAQs */}
          {activeFaqTab === 'privacy' && (
          <div className="space-y-4">
            {/* Privacy FAQ Item 1 */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleFaq(0)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheckMarkIcon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  <h4 className="text-lg font-semibold text-slate-900">What data does StockSentry collect?</h4>
                </div>
                {openFaqIndex === 0 ? (
                  <ChevronUpIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                )}
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openFaqIndex === 0 ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6">
                  <p className="text-slate-600 mb-3">
                    StockSentry only collects the minimum data necessary to provide inventory alerts:
                  </p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Product inventory levels and SKUs</li>
                    <li>Historical sales velocity data</li>
                    <li>Store configuration and alert preferences</li>
                    <li>Contact information for notifications (email, phone if SMS enabled)</li>
                  </ul>
                  <p className="text-slate-600 mt-3">
                    We <strong className="text-slate-900">do not</strong> collect or store customer names, addresses, payment information, or any personally identifiable information beyond what's needed for notifications.
                  </p>
                </div>
              </div>
            </div>

            {/* Privacy FAQ Item 2 */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleFaq(1)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheckMarkIcon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  <h4 className="text-lg font-semibold text-slate-900">How is my data protected?</h4>
                </div>
                {openFaqIndex === 1 ? (
                  <ChevronUpIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                )}
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openFaqIndex === 1 ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6">
                  <p className="text-slate-600 mb-3">
                    We implement industry-standard security measures in compliance with Shopify's protected customer data requirements:
                  </p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li><strong className="text-slate-900">Encryption:</strong> All data is encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
                    <li><strong className="text-slate-900">Access Controls:</strong> Role-based access with multi-factor authentication for our team</li>
                    <li><strong className="text-slate-900">Data Separation:</strong> Production and test environments are completely isolated</li>
                    <li><strong className="text-slate-900">Monitoring:</strong> Continuous security monitoring with automated threat detection</li>
                    <li><strong className="text-slate-900">Auditing:</strong> Comprehensive access logs maintained for security reviews</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Privacy FAQ Item 3 */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleFaq(2)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheckMarkIcon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  <h4 className="text-lg font-semibold text-slate-900">Do you share my data with third parties?</h4>
                </div>
                {openFaqIndex === 2 ? (
                  <ChevronUpIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                )}
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openFaqIndex === 2 ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6">
                  <p className="text-slate-600 mb-3">
                    We <strong className="text-slate-900">never sell your data</strong>. Limited data sharing only occurs when necessary for app functionality:
                  </p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li><strong className="text-slate-900">Notification Services:</strong> Email/SMS providers to deliver alerts (only contact info shared)</li>
                    <li><strong className="text-slate-900">Infrastructure Providers:</strong> Secure cloud hosting (AWS/GCP) with encryption and access controls</li>
                    <li><strong className="text-slate-900">Analytics:</strong> Anonymized usage data to improve the app (no personal information)</li>
                  </ul>
                  <p className="text-slate-600 mt-3">
                    All third-party providers are vetted, GDPR-compliant, and bound by strict data processing agreements.
                  </p>
                </div>
              </div>
            </div>

            {/* Privacy FAQ Item 4 */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleFaq(3)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheckMarkIcon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  <h4 className="text-lg font-semibold text-slate-900">How long do you retain my data?</h4>
                </div>
                {openFaqIndex === 3 ? (
                  <ChevronUpIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                )}
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openFaqIndex === 3 ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6">
                  <p className="text-slate-600 mb-3">
                    We retain data only as long as necessary for providing our service:
                  </p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li><strong className="text-slate-900">Active Accounts:</strong> Historical sales velocity data (up to 24 months for seasonal analysis)</li>
                    <li><strong className="text-slate-900">Uninstallation:</strong> All data deleted within 30 days of app removal</li>
                    <li><strong className="text-slate-900">Account Deletion:</strong> Complete data erasure within 48 hours upon request</li>
                    <li><strong className="text-slate-900">Backups:</strong> Encrypted backups retained for 90 days for disaster recovery, then permanently deleted</li>
                  </ul>
                  <p className="text-slate-600 mt-3">
                    You can request immediate data deletion at any time by contacting{' '}
                    <a href="mailto:support@retailforge.app" className="text-blue-600 hover:text-blue-700 font-medium underline">
                      support@retailforge.app
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Privacy FAQ Item 5 */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleFaq(4)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheckMarkIcon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  <h4 className="text-lg font-semibold text-slate-900">Are you GDPR and CCPA compliant?</h4>
                </div>
                {openFaqIndex === 4 ? (
                  <ChevronUpIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                )}
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openFaqIndex === 4 ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6">
                  <p className="text-slate-600 mb-3">
                    Yes. We comply with major data protection regulations including:
                  </p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li><strong className="text-slate-900">GDPR (EU):</strong> Right to access, rectify, erase, and port your data</li>
                    <li><strong className="text-slate-900">CCPA/CPRA (California):</strong> Right to know, delete, and opt-out of data sales</li>
                    <li><strong className="text-slate-900">PIPEDA (Canada):</strong> Consent-based data collection with accountability</li>
                    <li><strong className="text-slate-900">Shopify Requirements:</strong> Full compliance with protected customer data policies</li>
                  </ul>
                  <p className="text-slate-600 mt-3">
                    To exercise your data rights, contact{' '}
                    <a href="mailto:support@retailforge.app" className="text-blue-600 hover:text-blue-700 font-medium underline">
                      support@retailforge.app
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Privacy FAQ Item 6 */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleFaq(5)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheckMarkIcon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  <h4 className="text-lg font-semibold text-slate-900">What happens if there's a security incident?</h4>
                </div>
                {openFaqIndex === 5 ? (
                  <ChevronUpIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-slate-500 flex-shrink-0 ml-4" />
                )}
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openFaqIndex === 5 ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6">
                  <p className="text-slate-600 mb-3">
                    We have a comprehensive security incident response plan:
                  </p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li><strong className="text-slate-900">Immediate Response:</strong> Automated detection and containment within minutes</li>
                    <li><strong className="text-slate-900">Notification:</strong> Affected merchants notified within 72 hours as required by GDPR</li>
                    <li><strong className="text-slate-900">Investigation:</strong> Full forensic analysis to determine scope and impact</li>
                    <li><strong className="text-slate-900">Remediation:</strong> Security vulnerabilities patched immediately</li>
                    <li><strong className="text-slate-900">Reporting:</strong> Regulatory authorities notified as required by law</li>
                  </ul>
                  <p className="text-slate-600 mt-3">
                    Report security concerns to{' '}
                    <a href="mailto:support@retailforge.app" className="text-blue-600 hover:text-blue-700 font-medium underline">
                      support@retailforge.app
                    </a>
                  </p>
                </div>
              </div>
            </div>

          </div>
          )}

          {/* Privacy Policy Link - shown on privacy tab */}
          {activeFaqTab === 'privacy' && (
            <div className="mt-8 pt-8 border-t border-slate-200 text-center">
              <p className="text-slate-600">
                For complete details, read our{' '}
                <a href="/privacy-policy" className="text-blue-600 hover:text-blue-700 font-semibold underline">
                  Privacy Policy
                </a>
                {' '}and{' '}
                <a href="/terms-of-service" className="text-blue-600 hover:text-blue-700 font-semibold underline">
                  Terms of Service
                </a>
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Waitlist Section */}
      <section id="waitlist" className="py-16 px-6 bg-blue-600">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Be First to Know</h2>
          <p className="text-blue-100 mb-8 text-lg">
            Join our waitlist to get early access to StockSentry and exclusive launch pricing.
          </p>
          
          {!submitted ? (
            <div>
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-4 focus:ring-blue-300 focus:outline-none disabled:bg-slate-100 disabled:cursor-not-allowed"
                  disabled={loading}
                  onKeyDown={(e) => e.key === 'Enter' && !loading && handleWaitlistSubmit(e)}
                />
                <button
                  onClick={handleWaitlistSubmit}
                  disabled={loading}
                  className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Joining...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-6 h-6 text-white fill-current" />
                      Join Waitlist
                    </>
                  )}
                </button>
              </div>

              {/* Cloudflare Turnstile CAPTCHA */}
              <div className={`mt-4 flex justify-center ${!captchaLoaded ? 'h-0 overflow-hidden' : ''}`}>
                <Turnstile
                  siteKey={'0x4AAAAAAB5LWIXIMN-gUrwd'}
                  onSuccess={(token) => {
                    setCaptchaToken(token);
                    setError('');
                    setCaptchaLoaded(true);
                  }}
                  onError={() => {
                    setCaptchaToken('');
                    setError('Failed to verify security check. Please try again.');
                    setCaptchaLoaded(true);
                  }}
                  onExpire={() => {
                    setCaptchaToken('');
                  }}
                  onLoad={() => {
                    setCaptchaLoaded(true);
                  }}
                  options={{
                    theme: 'light',
                    size: 'normal',
                    action: 'newsletter-signup',
                    execution: 'render',
                    appearance: 'interaction-only',
                    retry: 'auto',
                  }}
                />
              </div>

              {error && (
                <div className="mt-4 max-w-md mx-auto bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg animate-fadeIn">
                  <p className="text-sm">{error}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-blue-500 rounded-lg p-6 max-w-md mx-auto">
              <div className="flex items-center justify-center w-12 h-12 bg-green-500 rounded-full mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-white font-semibold mb-2">You're on the list!</h4>
              <p className="text-blue-100 text-sm">We'll notify you as soon as StockSentry launches.</p>
            </div>
          )}
          
          <p className="text-blue-200 text-sm mt-4">
            No spam, ever. Unsubscribe with one click.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="py-12 px-6 bg-slate-900">
        <div className="max-w-6xl mx-auto">

          <div className="grid md:grid-cols-4 gap-12 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/retailforge-logo.png"
                  alt="RetailForge - Shopify Partner Building E-Commerce Solutions"
                  className="h-12 w-auto"
                />
                <div>
                  <h4 className="text-xl font-semibold text-white">RetailForge</h4>
                  <p className="text-slate-400 text-sm uppercase tracking-wide">Solutions for the modern storefront</p>
                </div>
              </div>
              <p className="text-slate-300 mb-4 max-w-md">
                Building smart tools for modern merchants. Forged by retail experience,
                powered by intelligent technology.
              </p>
            </div>

            <div>
              <h5 className="text-white font-semibold mb-4">Legal</h5>
              <ul className="space-y-3">
                <li>
                  <Link to="/privacy-policy" className="text-slate-300 hover:text-white transition-colors duration-200 flex items-center gap-2 group">
                    <ShieldCheckMarkIcon className="w-4 h-4 fill-current text-slate-300 group-hover:text-white" />
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className="text-slate-300 hover:text-white transition-colors duration-200 flex items-center gap-2 group">
                    <ShieldCheckMarkIcon className="w-4 h-4 fill-current text-slate-300 group-hover:text-white" />
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h5 className="text-white font-semibold mb-4">Help & Support</h5>
              <ul className="space-y-3">
                <li>
                  <a href="#faq" onClick={(e) => handleSmoothScroll(e, 'faq')} className="text-slate-300 hover:text-white transition-colors duration-200 flex items-center gap-2 group">
                    <QuestionCircleIcon className="w-4 h-4 fill-current text-slate-300 group-hover:text-white" />
                    Help Center
                  </a>
                </li>
                <li>
                  <a href="mailto:support@retailforge.app" className="text-slate-300 hover:text-white transition-colors duration-200 flex items-center gap-2 group">
                    <ChatIcon className="w-4 h-4 fill-current text-slate-300 group-hover:text-white" />
                    Contact Support
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8">
            <div className="flex justify-center">
              <a href="https://www.shopify.com/ca/partners" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 bg-white px-6 py-4 rounded-lg shadow-lg border-2 border-slate-200 hover:shadow-xl hover:border-blue-300 transition-all duration-200 group">
                <img
                  src="/shopify-logo.svg"
                  alt="Shopify Partner Badge - Official App Development Partner"
                  className="h-8"
                />
                <div className="border-l-2 border-slate-300 pl-4">
                  <p className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors duration-200">Official Shopify Partner</p>
                  <p className="text-xs text-slate-600">App Development & Solutions</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RetailForgeLanding;